import { RcsMessageButtonTypes, RcsMessageCategories } from 'constants/rcs';
import {
  RcsMessage,
  RcsMessageButton,
  RcsMessageButtonType,
  RcsMessageCard,
  RcsMessageCategory,
  RcsMessageClickToReplyButton,
  RcsMessageClickToReplyButtonBody,
  RcsMessageOpenLinkButton,
  RcsMessageOpenLinkButtonBody,
} from 'types/rcs';

export const isRcsMessageCategory = (
  category: unknown,
): category is RcsMessageCategory => {
  return Object.values<unknown>(RcsMessageCategories).includes(category);
};

export const isRcsMessageButtonType = (
  type: unknown,
): type is RcsMessageButtonType => {
  return Object.values<unknown>(RcsMessageButtonTypes).includes(type);
};

export const isRcsMessageClickToReplyButtonBody = (
  body: unknown,
): body is RcsMessageClickToReplyButtonBody => {
  const bodyValue = body as RcsMessageClickToReplyButtonBody;

  if (!bodyValue || typeof bodyValue !== 'object') return false;
  if (typeof bodyValue.postbackData !== 'string') return false;
  if (typeof bodyValue.text !== 'string') return false;

  return true;
};

export const isRcsMessageOpenLinkButtonBody = (
  body: unknown,
): body is RcsMessageOpenLinkButtonBody => {
  const bodyValue = body as RcsMessageOpenLinkButtonBody;

  if (!bodyValue || typeof bodyValue !== 'object') return false;
  if (typeof bodyValue.text !== 'string') return false;
  if (typeof bodyValue.url !== 'string') return false;

  return true;
};

export const isRcsMessageClickToReplyButton = (
  button: unknown,
): button is RcsMessageClickToReplyButton => {
  const buttonValue = button as RcsMessageClickToReplyButton;

  if (!buttonValue || typeof buttonValue !== 'object') return false;
  if (!isRcsMessageClickToReplyButtonBody(buttonValue.body)) return false;
  if (buttonValue.type !== RcsMessageButtonTypes.CLICK_TO_REPLY) return false;

  return true;
};

export const isRcsMessageOpenLinkButton = (
  button: unknown,
): button is RcsMessageOpenLinkButton => {
  const buttonValue = button as RcsMessageOpenLinkButton;

  if (!buttonValue || typeof buttonValue !== 'object') return false;
  if (!isRcsMessageOpenLinkButtonBody(buttonValue.body)) return false;
  if (buttonValue.type !== RcsMessageButtonTypes.OPEN_URL) return false;

  return true;
};

export const isRcsMessageButton = (
  button: unknown,
): button is RcsMessageButton => {
  return (
    isRcsMessageClickToReplyButton(button) || isRcsMessageOpenLinkButton(button)
  );
};

export const isRcsMessageCard = (card: unknown): card is RcsMessageCard => {
  const cardValue = card as RcsMessageCard;

  if (!cardValue || typeof cardValue !== 'object') return false;
  if (!Array.isArray(cardValue.buttons)) return false;
  if (cardValue.buttons.some((button) => !isRcsMessageButton(button)))
    return false;
  if (typeof cardValue.text !== 'string') return false;
  if (
    typeof cardValue.title !== 'string' &&
    cardValue.title !== null &&
    cardValue.title !== undefined
  )
    return false;
  if (typeof cardValue.url !== 'string') return false;

  return true;
};

export const isRcsMessage = (message: unknown): message is RcsMessage => {
  const messageValue = message as RcsMessage;

  if (!messageValue || typeof messageValue !== 'object') return false;
  if (!Array.isArray(messageValue.cards)) return false;
  if (messageValue.cards.some((card) => !isRcsMessageCard(card))) return false;
  if (!isRcsMessageCategory(messageValue.category)) return false;
  if (typeof messageValue.content !== 'string') return false;
  if (
    typeof messageValue.mediaUrl !== 'string' &&
    messageValue.mediaUrl !== null &&
    messageValue.mediaUrl !== undefined
  )
    return false;
  if (!Array.isArray(messageValue.suggestedActions)) return false;
  if (
    messageValue.suggestedActions.some((action) => !isRcsMessageButton(action))
  )
    return false;

  return true;
};
